import React, { useState, useMemo } from 'react';
import { Grid, Typography, LinearProgress, Table, TableBody, TableCell, TableContainer, TableRow, Paper } from '@mui/material';
import RCPieChart from '../../../../components/charts/recharts/rcPieChart';
import { buildQueryString, dataProvider } from '../../../../DataProvider';
import { useNotify } from 'react-admin';

/**
 * Chart view component for Difficult Event Tracking Report
 * Displays PIE chart showing venue count distribution across market types
 */
interface IData {
    [key: string]: any;
}

const ChartView: React.FC<IData> = (props) => {
    const { chart } = props;
    const filter = chart.filterList; // Get filters from chart props
    const notify = useNotify();
    const [isLoading, setIsLoading] = React.useState(false);
    const [data, setData] = useState<any[]>([]);
    const [originalData, setOriginalData] = useState<any[]>([]);

    /**
     * Custom tooltip component that shows detailed bypass information
     */
    const CustomTooltip = ({ active, payload }: any) => {
        if (!(active && payload && payload.length)) return null;
        const dataPoint = payload[0];
        const seriesInfo = sortedSeriesList.find((s: any) => s.name === dataPoint.name);
        const bypassInfo = seriesInfo?.bypassInfo;
        if (!bypassInfo) return null; // Only show bypass info

        return (
            <TableContainer component={Paper} sx={{ maxWidth: '100%', boxShadow: 3 }}>
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <svg height={12} width={12}>
                                        <circle cx={6} cy={6} r={6} style={{ fill: dataPoint.payload.fill }} />
                                    </svg>
                                    Bypass Information
                                </div>
                            </TableCell>
                        </TableRow>
                        {bypassInfo.browser_Name && (
                            <TableRow>
                                <TableCell><strong>Browser</strong></TableCell>
                                <TableCell>{bypassInfo.browser_Name}</TableCell>
                            </TableRow>
                        )}
                        {bypassInfo.ip_Type_Name && (
                            <TableRow>
                                <TableCell><strong>IP Type</strong></TableCell>
                                <TableCell>{bypassInfo.ip_Type_Name}</TableCell>
                            </TableRow>
                        )}
                        {bypassInfo.card_Brand_Name && (
                            <TableRow>
                                <TableCell><strong>Card Brand</strong></TableCell>
                                <TableCell>{bypassInfo.card_Brand_Name}</TableCell>
                            </TableRow>
                        )}
                        {bypassInfo.account_Type_Name && (
                            <TableRow>
                                <TableCell><strong>Account Type</strong></TableCell>
                                <TableCell>{bypassInfo.account_Type_Name}</TableCell>
                            </TableRow>
                        )}
                        {bypassInfo.primary_Account && (
                            <TableRow>
                                <TableCell><strong>Primary Account</strong></TableCell>
                                <TableCell>{bypassInfo.primary_Account}</TableCell>
                            </TableRow>
                        )}
                        {bypassInfo.remote_Comp !== undefined && bypassInfo.remote_Comp !== null && (
                            <TableRow>
                                <TableCell><strong>Remote Computer</strong></TableCell>
                                <TableCell>{bypassInfo.remote_Comp ? 'True' : 'False'}</TableCell>
                            </TableRow>
                        )}
                        {bypassInfo.other_State && (
                            <TableRow>
                                <TableCell><strong>Other State</strong></TableCell>
                                <TableCell>{bypassInfo.other_State}</TableCell>
                            </TableRow>
                        )}
                        {bypassInfo.zipCode && (
                            <TableRow>
                                <TableCell><strong>Zip Code</strong></TableCell>
                                <TableCell>{bypassInfo.zipCode}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    React.useEffect(() => {
        if (!chart.open) return;
        setIsLoading(true);
        dataProvider.fetchData(
            'DifficultEventTrackingReport',
            `GetGroupedByMarketType/Filter${convertFilter(filter)}`
        )
            .then((result: any) => {
                setOriginalData(result.data);
                setData(convertData(result.data));
            })
            .catch((err: any) => {
                notify(`Fail to fetch chart data: ${err}`, { type: 'error', autoHideDuration: 2000 });
            })
            .finally(() => setIsLoading(false));
    }, [chart.open, filter, notify]);

    const convertFilter = (flt: any) => buildQueryString({ filter: flt }, { orderBy: 'type' });

    /**
     * convertData
     *  - Builds the data array consumed by <RCPieChart />.
     *  - "total" (and displayName) reflects ALL venues for that market (not just the ones we draw) so the user sees the full scale.
     *  - We only attach keys for the venues we actually render (top 5 by count + any UnprocessedOrders) so the pie slices match the legend.
     */
    const convertData = (apiData: any) => (!apiData || !Array.isArray(apiData)) ? [] : apiData
        .map((it: any) => {
            const venues = Array.isArray(it.venues) ? it.venues : [];
            const totalAll = venues.reduce((sum: any, v: any) => sum + (v.venueCount || 0), 0); // total of all venues
            // Determine displayed venues (top 5 + unprocessed) for slices
            const processVenues = venues.filter((v: any) => v.category !== 'UnprocessedOrders');
            const top5 = [...processVenues].sort((a: any, b: any) => b.venueCount - a.venueCount).slice(0, 5);
            const unprocessed = venues.filter((v: any) => v.category === 'UnprocessedOrders');
            const displayed = [...top5, ...unprocessed];

            const row: IData = { name: it.marketType, displayName: `${it.marketType} (${totalAll})`, total: totalAll };
            displayed.forEach((v: any) => { row[v.venue] = v.venueCount; });
            return row;
        })
        .sort((a: any, b: any) => b.total - a.total);

    /**
     * Generate color palette for PIE chart slices
     */
    const baseColors = ['#808080', '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1', '#d084d0', '#ffb347', '#87ceeb', '#dda0dd', '#98fb98'];
    const generateColors = (len: any) => Array.from({ length: len }, (_: any, i: any) => baseColors[i % baseColors.length]);
    // Generate colors and series list for the chart
    // all venues: [{ venue: "ShoWare Center - Kent, WA", venueCount: 1, marketType: "accesso" }, ...]
    /**
     * allVenues
     *  - Normalized list of (venue, category) pairs we intend to visualize (top 5 + unprocessed per market).
     *  - Map ensures uniqueness when the same venue appears across markets/categories.
     */
    const allVenues = useMemo(() => {
        const map = new Map();
        originalData.forEach((mt: any) => {
            if (!Array.isArray(mt.venues)) return;
            const top = [...mt.venues].sort((a: any, b: any) => b.venueCount - a.venueCount).slice(0, 5);
            const unprocessed = mt.venues.filter((v: any) => v.category === 'UnprocessedOrders');
            [...top, ...unprocessed].forEach((v: any) => {
                const key = v.venue + '_' + v.category;
                if (!map.has(key)) {
                    map.set(key, {
                        venue: v.venue,
                        venueCount: v.venueCount,
                        marketType: mt.marketType || mt.name,
                        category: v.category,
                        bypassInfo: v.bypassInfo
                    });
                }
            });
        });
        return Array.from(map.values());
    }, [originalData]);

    const colors = useMemo(() => generateColors(allVenues.length), [allVenues]);

    /**
     * sortedSeriesList
     *  - Derives the series (legend items & slice meta) from allVenues.
     *  - Pinned ordering: UnprocessedOrders always at top, then by venueCount desc, then most recent bypassInfo timestamp.
     */
    const sortedSeriesList = useMemo(() => {
        let colorIdx = 0;
        const series = allVenues.map((v: any) => {
            const fill = v.category === 'UnprocessedOrders'
                ? '#ff4444'
                : colors[(colorIdx++) % colors.length];
            return {
                dataKey: v.venue,
                name: v.category === 'UnprocessedOrders' ? `Unprocessed - ${v.venue}` : v.venue,
                fill,
                marketType: v.marketType,
                venueCount: v.venueCount,
                category: v.category,
                bypassInfo: v.bypassInfo
            };
        });
        // Sort: primary venueCount desc, secondary utc_Posted_On desc (recent first)
        return series.sort((a: any, b: any) => {
            const aUn = a.category === 'UnprocessedOrders';
            const bUn = b.category === 'UnprocessedOrders';
            if (aUn && !bUn) return -1; // pin UnprocessedOrders on top
            if (!aUn && bUn) return 1;
            const countDiff = b.venueCount - a.venueCount;
            if (countDiff !== 0) return countDiff;
            const ad = a.bypassInfo?.utc_Posted_On ? new Date(a.bypassInfo.utc_Posted_On).getTime() : 0;
            const bd = b.bypassInfo?.utc_Posted_On ? new Date(b.bypassInfo.utc_Posted_On).getTime() : 0;
            return bd - ad;
        });
    }, [allVenues, colors]);

    // customConvertData: filter series to only those belonging to the market row & map to recharts slice shape
    const customConvertData = (item: any) => sortedSeriesList
        .filter((s: any) => s.marketType === item.name)
        .map((s: any) => ({ name: s.name, value: item[s.dataKey], fill: s.fill }));
    
    return (
        <>
            {chart.open ? (
                <>
                    {isLoading && <LinearProgress color='secondary' />}
                    <Typography sx={{ marginBottom: '20px' }} variant="h4" textAlign={'center'}>
                        Venue Count Distribution by Market Type
                    </Typography>
                    {data.length > 0 ? (
                        <Grid container alignItems="center" sx={{ minHeight: '65vh' }}
                            justifyContent="center" spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                            <RCPieChart
                                data={data}
                                dataKey={'displayName'}
                                height={250}
                                width={550}
                                chartSize={80}
                                elevation={10}
                                seriesList={sortedSeriesList}
                                convertData={customConvertData}
                                customTooltip={CustomTooltip}
                                showLegendCounts={true}
                                enableLegendClickTooltip={true}
                                legend={{ layout: 'vertical', align: 'right', verticalAlign: 'middle', iconType: 'square', wrapperStyle: { maxHeight: 300, overflowY: 'auto', paddingRight: 8, minWidth: 300 } }}
                            />
                        </Grid>
                    ) : (!isLoading && (
                        <Typography variant="h6" textAlign={'center'} sx={{ padding: '40px' }}>
                            No data available for chart visualization
                        </Typography>
                    ))}
                </>
            ) : (
                <Typography variant="h6" textAlign={'center'} sx={{ padding: '40px', color: 'gray' }}>
                    Please apply filters and click search to view the chart
                </Typography>
            )}
        </>
    );
};

export default ChartView;
